import { TextFieldProps, Box } from '@mui/material';
import { useState, useEffect } from 'react';
import { useLoader } from '@/contexts/loader/LoaderContext';
import { User, UpdateUserRequest } from '@/models/company';
import { Validator, useErrorHandling } from '@/utils/errorHandling';
import UsersAPIServiceV2 from '@/services/UserServiceV2';
import {
    StyledDialog,
    StyledDialogContainer,
    StyledDialogTitle
} from '@/components/consent/StyledDialog';
import { SemiBold, SMRegular } from '@/typography/Typography';
import { t } from 'i18next';
import { InputText } from '@/components/shared/InputText';
import {
    PrimaryButtonStyle,
    SecondaryButtonStyle
} from '@/pages/company/common/IntegrationDialog';
import MobileInput from '@/components/shared/MobileInput';

type UpdateUserDialogProps = {
    userId?: string;
    open: boolean;
    onClose: () => void;
    onSubmit: (request: UpdateUserRequest) => Promise<void>;
    validator: Validator;
    setValidator: React.Dispatch<React.SetStateAction<Validator>>;
};

export const UpdateUserDialog = (props: UpdateUserDialogProps) => {
    const [firstName, setFirstName] = useState<string>('');
    const [lastName, setLastName] = useState<string>('');
    const [email, setEmail] = useState<string>('');
    const [phoneNumber, setPhoneNumber] = useState<string>('');
    const [user, setUser] = useState<User>();
    const { setLoading } = useLoader();

    const { backendErrorHandler } = useErrorHandling();

    const onSubmit = () => {
        if (!props.userId) return;

        props.onSubmit({
            firstName: firstName,
            lastName: lastName,
            phoneNumber: phoneNumber,
            email: email,
            id: props.userId
        });
    };

    useEffect(() => {
        if (!props.userId) return;

        setLoading(true);

        UsersAPIServiceV2.getInstance()
            .getUser(props.userId)
            .then((response: any) => {
                let responseUser = response.data as User;
                setUser(responseUser);
                setFirstName(responseUser.firstName);
                setLastName(responseUser.lastName);
                setPhoneNumber(responseUser.phoneNumber);
                setEmail(responseUser.email);
            })
            .catch(backendErrorHandler('Error fetching user: '))
            .finally(() => {
                setLoading(false);
            });
    }, [props.userId, backendErrorHandler, setLoading]);

    const commonProps: TextFieldProps = {
        margin: 'dense',
        fullWidth: true,
        variant: 'standard'
    };

    return (
        <StyledDialog open={props.open} onClose={props.onClose}>
            <StyledDialogContainer style={{ width: '400px' }}>
                <StyledDialogTitle>
                    <SemiBold>{t('edit_employee')}</SemiBold>
                    <SMRegular m={1}>
                        {user?.firstName} {user?.lastName}
                    </SMRegular>
                </StyledDialogTitle>

                <div>
                    <InputText
                        value={firstName}
                        label="First name"
                        name="first_name"
                        error={props.validator.hasError('firstName')}
                        helperText={props.validator.getError('firstName')}
                        onChange={(e) => {
                            setFirstName(e.currentTarget.value);
                            props.setValidator(
                                props.validator.clearError('firstName')
                            );
                        }}
                        spacing="10px 0"
                    />

                    <InputText
                        name="last_name"
                        label="Last name"
                        value={lastName}
                        error={props.validator.hasError('lastName')}
                        helperText={props.validator.getError('lastName')}
                        onChange={(e) => {
                            setLastName(e.currentTarget.value);
                            props.setValidator(
                                props.validator.clearError('lastName')
                            );
                        }}
                        spacing="10px 0"
                    />
                    <MobileInput
                        name="phone_number"
                        label="Phone number"
                        placeholder="+XX XXXXXX"
                        error={props.validator.hasError('phoneNumber')}
                        helperText={
                            props.validator.getError(
                                'phoneNumber',
                                'Phone number including country code'
                            )!
                        }
                        value={phoneNumber}
                        onChange={(e) => {
                            setPhoneNumber(e);
                            props.setValidator(
                                props.validator.clearError('phoneNumber')
                            );
                        }}
                    />

                    <InputText
                        name="email"
                        label="Email"
                        type="email"
                        value={email}
                        error={props.validator.hasError('email')}
                        helperText={props.validator.getError('email')}
                        onChange={(e) => {
                            setEmail(e.currentTarget.value);
                            props.setValidator(
                                props.validator.clearError('email')
                            );
                        }}
                        spacing="10px 0"
                    />

                    <Box mt={1} mb={1} textAlign={'center'}>
                        <SMRegular
                            color="secondary"
                            style={{ fontWeight: 'bold' }}
                            display={'inline'}
                        >
                            Note:{' '}
                        </SMRegular>
                        <SMRegular color="secondary" display={'inline'}>
                            The changes you do here may be overwritten during
                            import of users from the salary provider. Please fix
                            the data in the salary provider system if needed.
                        </SMRegular>
                    </Box>
                </div>

                <Box
                    gap={1}
                    mt={2}
                    display={'flex'}
                    justifyContent={'space-between'}
                >
                    <SecondaryButtonStyle onClick={props.onClose}>
                        {t('cancel')}
                    </SecondaryButtonStyle>

                    <PrimaryButtonStyle onClick={onSubmit} size="medium">
                        {t('save')}
                    </PrimaryButtonStyle>
                </Box>
            </StyledDialogContainer>
        </StyledDialog>
    );
};
